import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Card } from '@/design-system/card';
import { cn } from '@/design-system/lib/utils';
import { Paginator } from '@/design-system/pagination';
import { SearchField } from '@/design-system/search-field';
import { SearchSelect } from '@/design-system/search-select';
import { Skeleton } from '@/design-system/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/design-system/table';
import { toast } from '@/design-system/toaster';
import {
  RiderPerformanceRow,
  RiderPerformanceSummary,
  invoicesAPI,
} from '@/services/api/invoices';

import RiderDetailPanel from './RiderDetailPanel';
import { useSearchableOptions } from './useSearchableOptions';

interface PerformancePanelProps {
  /** Jump to the board filtered by this rider (the drawer's "View in status board"). */
  onOpenBoardForRider: (driverId: string, driverName: string) => void;
}

const DEFAULT_PAGE_SIZE = 20;

const numCls = 'text-[13px] tabular-nums text-text-strong-950';
const zeroCls = 'text-[13px] tabular-nums text-text-soft-400';

const Num: React.FC<{ n: number; strong?: boolean }> = ({ n, strong }) => (
  <span
    className={n === 0 ? zeroCls : strong ? `${numCls} font-semibold` : numCls}
  >
    {n}
  </span>
);

// ── Grouped-header column model ──────────────────────────────────────────
// The rider table carries a two-tier header (mirrors the prototype): a top row
// of group labels — "Posisi fisik", "Status lifecycle", "Aging & risiko" —
// spanning the real columns below. Columns without a `group` (Rider, Needs
// follow-up) sit in the leading block with no label above them.
type PerfColGroup = 'posisi' | 'status' | 'aging';

interface PerfColumn {
  key: string;
  title: string;
  /** Undefined → leading (ungrouped) column. */
  group?: PerfColGroup;
  render: (r: RiderPerformanceRow) => React.ReactNode;
}

const PERF_GROUPS: { key: PerfColGroup; label: string }[] = [
  { key: 'posisi', label: 'Posisi invoice' },
  { key: 'status', label: 'Status lifecycle' },
  { key: 'aging', label: 'Aging & risiko' },
];

const PERF_COLUMNS: PerfColumn[] = [
  {
    key: 'rider',
    title: 'Rider',
    render: (r) => {
      const initial = r.driverName ? r.driverName.charAt(0).toUpperCase() : '·';
      return (
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-bg-weak-50 text-[11px] font-semibold text-text-sub-600">
            {initial}
          </div>
          <span className="text-[13px] tracking-tight text-text-strong-950">
            {r.driverName}
          </span>
        </div>
      );
    },
  },
  {
    // Ops priority: kept in the leading block (emphasis by position + weight,
    // not an always-on pill). Sits outside the three physical/lifecycle groups.
    key: 'unresolvedByRider',
    title: 'Needs follow-up',
    render: (r) =>
      r.unresolvedByRider > 0 ? (
        <span className="text-[15px] font-bold tabular-nums text-text-strong-950">
          {r.unresolvedByRider}
        </span>
      ) : (
        <span className={zeroCls}>0</span>
      ),
  },
  {
    key: 'held',
    title: 'Held',
    group: 'posisi',
    render: (r) => <Num n={r.held} strong />,
  },
  {
    key: 'oldest',
    title: 'Oldest',
    group: 'posisi',
    render: (r) => {
      if (!r.oldestIssueDate) return <span className={zeroCls}>—</span>;
      const overdue = (r.oldestDaysSince ?? 0) >= 7;
      return (
        <div>
          <span
            className={`text-[13px] tabular-nums ${
              overdue ? 'text-error-base font-semibold' : 'text-text-strong-950'
            }`}
          >
            {r.oldestIssueDate}
          </span>
          <p className="text-[11px] text-text-sub-600">
            {r.oldestDaysSince ?? 0} days
          </p>
        </div>
      );
    },
  },
  {
    key: 'byRider',
    title: 'By rider',
    group: 'status',
    render: (r) => <Num n={r.byRider} />,
  },
  {
    key: 'atHub',
    title: 'At hub',
    group: 'status',
    render: (r) => <Num n={r.atHub} />,
  },
  {
    key: 'awaiting',
    title: 'Awaiting',
    group: 'status',
    render: (r) => <Num n={r.awaiting} />,
  },
  {
    key: 'exception',
    title: 'Exception',
    group: 'status',
    render: (r) => <Num n={r.exception} />,
  },
  {
    key: 'disputed',
    title: 'Disputed',
    group: 'status',
    render: (r) => <Num n={r.disputed} />,
  },
  {
    key: 'resolved',
    title: 'Resolved',
    group: 'status',
    render: (r) => <Num n={r.resolved} />,
  },
  {
    key: 'red',
    title: 'Red',
    group: 'aging',
    render: (r) =>
      r.red > 0 ? (
        <span className="inline-flex items-center gap-1 rounded-md bg-error-lighter px-2 py-0.5 text-[12px] font-semibold tabular-nums text-error-base">
          {r.red}
        </span>
      ) : (
        <span className={zeroCls}>0</span>
      ),
  },
  {
    key: 'avgAging',
    title: 'Avg aging',
    group: 'aging',
    render: (r) => (
      <span className="text-[13px] tabular-nums text-text-sub-600">
        {r.avgAging}d
      </span>
    ),
  },
];

// Number of leading (ungrouped) columns and the keys that begin each group —
// used to draw the empty group-header spacer and the vertical group dividers.
const PERF_LEADING_COUNT = PERF_COLUMNS.filter((c) => !c.group).length;
const PERF_GROUP_START_KEYS = new Set(
  PERF_GROUPS.map(
    (g) => PERF_COLUMNS.find((c) => c.group === g.key)?.key
  ).filter((k): k is string => Boolean(k))
);

// Bespoke grouped-header table for the rider performance list. The shared
// `TableData` primitive renders a single header row and can't express the
// two-tier group header, so this page-specific layout is co-located here (per
// CLAUDE.md) using the same low-level DS table primitives TableData is built on.
const RiderPerformanceTable: React.FC<{
  rows: RiderPerformanceRow[];
  loading: boolean;
  onRowClick: (r: RiderPerformanceRow) => void;
}> = ({ rows, loading, onRowClick }) => (
  <div className="overflow-hidden rounded-lg border border-stroke-soft-200 bg-bg-white-0">
    <Table containerClassName="overflow-x-auto">
      <TableHeader>
        {/* Group label row */}
        <TableRow>
          <TableHead colSpan={PERF_LEADING_COUNT} aria-hidden />
          {PERF_GROUPS.map((g) => {
            const span = PERF_COLUMNS.filter((c) => c.group === g.key).length;
            return (
              <TableHead
                key={g.key}
                colSpan={span}
                className="h-8 border-l border-stroke-soft-200 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-soft-400"
              >
                {g.label}
              </TableHead>
            );
          })}
        </TableRow>
        {/* Column label row */}
        <TableRow>
          {PERF_COLUMNS.map((c) => (
            <TableHead
              key={c.key}
              className={cn(
                'whitespace-nowrap px-3 py-2 text-[11px] font-medium text-text-soft-400',
                PERF_GROUP_START_KEYS.has(c.key) &&
                  'border-l border-stroke-soft-200'
              )}
            >
              {c.title}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>

      <TableBody>
        {loading ? (
          [...Array(6)].map((_, i) => (
            <TableRow key={i}>
              {PERF_COLUMNS.map((c) => (
                <TableCell
                  key={c.key}
                  className={cn(
                    'px-3 py-2',
                    PERF_GROUP_START_KEYS.has(c.key) &&
                      'border-l border-stroke-soft-200'
                  )}
                >
                  <Skeleton className="h-4 w-full" />
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : rows.length === 0 ? (
          <TableRow className="hover:bg-transparent">
            <TableCell
              colSpan={PERF_COLUMNS.length}
              className="py-12 text-center text-sm text-text-sub-600"
            >
              No riders found.
            </TableCell>
          </TableRow>
        ) : (
          rows.map((r, i) => (
            <TableRow
              key={r.driverId ?? `${r.driverName}-${i}`}
              className="cursor-pointer"
              onClick={() => onRowClick(r)}
            >
              {PERF_COLUMNS.map((c) => (
                <TableCell
                  key={c.key}
                  className={cn(
                    'whitespace-nowrap px-3 py-2 text-xs',
                    PERF_GROUP_START_KEYS.has(c.key) &&
                      'border-l border-stroke-soft-200'
                  )}
                >
                  {c.render(r)}
                </TableCell>
              ))}
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </div>
);

const PerformancePanel: React.FC<PerformancePanelProps> = ({
  onOpenBoardForRider,
}) => {
  const hubs = useSearchableOptions(true, invoicesAPI.hubs);
  const [hubId, setHubId] = useState<string>('');

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Hub-wide KPI cards — separate endpoint, stable across page/search.
  const [summary, setSummary] = useState<RiderPerformanceSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Paginated rider table.
  const [rows, setRows] = useState<RiderPerformanceRow[]>([]);
  const [asOf, setAsOf] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);

  const [selectedRider, setSelectedRider] =
    useState<RiderPerformanceRow | null>(null);

  // Auto-select the first hub once options load so the view is never empty.
  useEffect(() => {
    if (!hubId && hubs.options.length > 0) {
      setHubId(String(hubs.options[0].id));
    }
  }, [hubId, hubs.options]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 500);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 1 whenever the hub or search changes.
  useEffect(() => {
    setPage(1);
  }, [hubId, debouncedSearch]);

  // Hub-wide KPI summary — refetched only when the hub changes.
  const fetchSummary = useCallback(async () => {
    if (!hubId) {
      setSummary(null);
      return;
    }
    setSummaryLoading(true);
    try {
      setSummary(await invoicesAPI.riderPerformanceSummary(hubId));
    } catch (err) {
      setSummary(null);
      toast.error('Could not load hub summary', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setSummaryLoading(false);
    }
  }, [hubId]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Paginated rider rows.
  const fetchRiders = useCallback(async () => {
    if (!hubId) {
      setRows([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    try {
      const data = await invoicesAPI.riderPerformance({
        hubId,
        search: debouncedSearch || undefined,
        page,
        pageSize,
      });
      setRows(data.data);
      setTotal(data.total);
      setAsOf(data.asOf);
    } catch (err) {
      setRows([]);
      setTotal(0);
      toast.error('Could not load rider performance', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setLoading(false);
    }
  }, [hubId, debouncedSearch, page, pageSize]);

  useEffect(() => {
    fetchRiders();
  }, [fetchRiders]);

  const hubName = useMemo(() => {
    if (!hubId) return '';
    return (
      hubs.options.find((h) => String(h.id) === hubId)?.name ??
      hubs.labelFor(hubId) ??
      hubId
    );
  }, [hubId, hubs]);

  return (
    <div className="space-y-5">
      <PerformanceKpis summary={summary} loading={summaryLoading} />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-semibold text-text-strong-950">
            Hub
          </span>
          <div className="w-[280px]">
            <SearchSelect
              options={hubs.options.map((h) => ({
                value: String(h.id),
                label: h.name,
              }))}
              value={hubId}
              onChange={(v) => setHubId(v)}
              onSearchChange={hubs.setSearch}
              disableLocalFilter
              loading={hubs.loading}
              placeholder="Select a hub…"
            />
          </div>
        </div>
        <SearchField
          value={search}
          onChange={setSearch}
          placeholder="Search rider…"
          size="sm"
          className="max-w-xs sm:flex-1"
        />
      </div>

      {asOf && (
        <p className="text-[11px] text-text-sub-600">
          Data as of{' '}
          {new Date(asOf).toLocaleString('en-US', {
            dateStyle: 'medium',
            timeStyle: 'short',
          })}{' '}
          · synced from hub system
        </p>
      )}

      <Card padding="none">
        <div className="p-4">
          <RiderPerformanceTable
            rows={rows}
            loading={loading}
            onRowClick={setSelectedRider}
          />

          {total > 0 && (
            <Paginator
              currentPage={page}
              totalItems={total}
              itemsPerPage={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          )}
        </div>
      </Card>

      <RiderDetailPanel
        rider={selectedRider}
        hubId={hubId}
        hubName={hubName}
        onClose={() => setSelectedRider(null)}
        onOpenBoard={(driverId, driverName) => {
          setSelectedRider(null);
          onOpenBoardForRider(driverId, driverName);
        }}
      />
    </div>
  );
};

// 6 KPI tiles — mirrors the board SummaryBar visual language.
const PerformanceKpis: React.FC<{
  summary: RiderPerformanceSummary | null;
  loading: boolean;
}> = ({ summary, loading }) => {
  const cards: {
    label: string;
    value: number | null;
    dot: string;
    sub?: string;
    alert?: boolean;
  }[] = [
    {
      label: 'Total invoices',
      value: summary?.totalInvoice ?? null,
      dot: 'bg-text-soft-400',
    },
    {
      label: 'At rider',
      value: summary?.atRider ?? null,
      dot: 'bg-(--state-warning-base)',
      sub: 'not yet back at hub',
    },
    {
      label: 'Received at hub',
      value: summary?.receivedAtHub ?? null,
      dot: 'bg-(--state-information-base)',
    },
    {
      label: 'Awaiting confirm',
      value: summary?.awaitingConfirmation ?? null,
      dot: 'bg-(--state-information-base)',
    },
    {
      label: 'Exception',
      value: summary?.exception ?? null,
      dot: 'bg-(--state-warning-base)',
    },
    {
      label: 'Red alerts',
      value: summary?.redAlerts ?? null,
      dot: 'bg-(--state-error-base)',
      sub: '7+ days',
      alert: (summary?.redAlerts ?? 0) > 0,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
      {cards.map((card) => (
        <div
          key={card.label}
          className={`rounded-lg border bg-bg-white-0 p-4 ${
            card.alert
              ? 'border-error-light bg-error-lighter/50'
              : 'border-stroke-soft-200'
          }`}
        >
          <div className="mb-3 flex items-center gap-2 label-eyebrow">
            <span className={`h-2 w-2 rounded-full ${card.dot}`} />
            {card.label}
          </div>
          <div
            className={`text-[28px] font-extrabold leading-none tracking-tight tabular-nums ${
              card.alert ? 'text-error-base' : 'text-text-strong-950'
            }`}
          >
            {loading || card.value === null ? (
              <span className="inline-block h-7 w-10 animate-pulse rounded-md bg-bg-soft-200" />
            ) : (
              card.value
            )}
          </div>
          {card.sub && (
            <p className="mt-3 text-[11px] text-text-sub-600 border-t border-stroke-soft-200 pt-2">
              {card.sub}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

export default PerformancePanel;
